import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/site";

const Logo = () => (
  <Link to="/" className="flex items-center" data-testid="nav-logo">
    <img
      src="/epg-logo-dark.png"
      alt="Executive Print Group"
      className="h-10 w-auto sm:h-12"
      data-testid="nav-logo-img"
    />
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <nav
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-50 border-b bg-white/90 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 ${
          scrolled ? "border-line shadow-[0_1px_12px_rgba(23,32,51,0.06)]" : "border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Logo />
          <div className="hidden items-center gap-8 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative py-1 text-sm font-medium transition-colors duration-200 after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:rounded-full after:bg-brand2 after:transition-[width] after:duration-300 ${
                    isActive ? "text-brand after:w-full" : "text-navy after:w-0 hover:text-brand hover:after:w-full"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" data-testid="nav-quote-btn" className="btn-primary ml-2 !px-5 !py-2.5">
              Request a Quote
            </Link>
          </div>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-line bg-white text-navy transition-colors hover:bg-tint lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 flex flex-col bg-white lg:hidden"
          >
            <div className="grid-lines absolute inset-0" />
            <div className="relative flex flex-1 flex-col justify-center gap-1 px-8 pt-20">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block border-b border-line py-4 font-display text-2xl font-bold tracking-tight ${
                        isActive ? "text-brand" : "text-navy"
                      }`
                    }
                  >
                    <span className="mr-4 text-xs font-semibold text-slate">0{i + 1}</span>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="pt-8"
              >
                <Link to="/contact" data-testid="mobile-nav-quote-btn" className="btn-primary">
                  Request a Quote
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
